import type { MetricName } from "schemas/generated/analyticsRead";

/**
 * Calculate the overall benchmarks for all replo stores for a given AOV bracket.
 *
 * First calculate the raw totals, ad then calculate overall ratios from aggregated totals
 *
 * @author Max 2025-06-24
 */
export function calculateOverallBenchmarks(aovBracket: string) {
  const filteredBenchmarks = BENCHMARKS.filter(([key, _]) => {
    return key.endsWith(`:${aovBracket}`);
  });

  const totals = filteredBenchmarks.reduce(
    (acc, [_, metrics]) => ({
      views: acc.views + metrics.views,
      unique_sessions: acc.unique_sessions + metrics.unique_sessions,
      conversions: acc.conversions + metrics.conversions,
      revenue: acc.revenue + metrics.revenue,
    }),
    { views: 0, unique_sessions: 0, conversions: 0, revenue: 0 },
  );

  return {
    views: totals.views,
    unique_sessions: totals.unique_sessions,
    conversions: totals.conversions,
    revenue: totals.revenue,
    conversion_rates: totals.conversions / totals.unique_sessions,
    revenue_per_session: totals.revenue / totals.unique_sessions,
    average_order_value: totals.revenue / totals.conversions,
  };
}

export const METRICS_REQUIRING_PRORATION: MetricName[] = [
  "unique_sessions",
  "views",
  "revenue",
  "conversions",
];

export const BENCHMARKS: [string, Record<MetricName, number>][] = [
  [
    "Sports / Sporting Goods:0-50",
    {
      views: 28652.5,
      unique_sessions: 18785.5,
      conversions: 564,
      revenue: 22959.646321840755,
      conversion_rates: 0.017288512023383804,
      revenue_per_session: 0.7404462330081398,
      average_order_value: 33.17739911303575,
    },
  ],
  [
    "Sports / Sporting Goods:50-100",
    {
      views: 23143,
      unique_sessions: 17939,
      conversions: 302,
      revenue: 16028.390000000001,
      conversion_rates: 0.0217152281473337,
      revenue_per_session: 1.4445094556720193,
      average_order_value: 66.52057468018741,
    },
  ],
  [
    "Sports / Sporting Goods:100+",
    {
      views: 37147,
      unique_sessions: 23030,
      conversions: 258,
      revenue: 34992.84199695635,
      conversion_rates: 0.00619514713474445,
      revenue_per_session: 2.2704092242659257,
      average_order_value: 281.98026924244925,
    },
  ],
  [
    "Pets & Animals / Pet Food & Supplies:0-50",
    {
      views: 17857.5,
      unique_sessions: 13016.5,
      conversions: 362,
      revenue: 12525.291705837939,
      conversion_rates: 0.02720331366371441,
      revenue_per_session: 0.9541620109949088,
      average_order_value: 38.78017204767335,
    },
  ],
  [
    "Pets & Animals / Pet Food & Supplies:50-100",
    {
      views: 35367,
      unique_sessions: 19741,
      conversions: 445,
      revenue: 25571.38,
      conversion_rates: 0.02270577105014191,
      revenue_per_session: 1.3251257647858599,
      average_order_value: 67.68161676646706,
    },
  ],
  [
    "Pets & Animals / Pet Food & Supplies:100+",
    {
      views: 35938.5,
      unique_sessions: 16268.5,
      conversions: 359,
      revenue: 41432.5018501719,
      conversion_rates: 0.02001832922200633,
      revenue_per_session: 2.1920443625189394,
      average_order_value: 113.27720918442446,
    },
  ],
  [
    "Home & Garden / Home Furnishings:0-50",
    {
      views: 102276,
      unique_sessions: 60266,
      conversions: 1390,
      revenue: 19168.920028491175,
      conversion_rates: 0.023064414429363158,
      revenue_per_session: 0.3180718817988779,
      average_order_value: 13.790589948554802,
    },
  ],
  [
    "Home & Garden / Home Furnishings:50-100",
    {
      views: 67836,
      unique_sessions: 44207.5,
      conversions: 907.5,
      revenue: 65817.45573686757,
      conversion_rates: 0.017338330238310457,
      revenue_per_session: 1.2561874414424008,
      average_order_value: 66.33357749816805,
    },
  ],
  [
    "Home & Garden / Home Furnishings:100+",
    {
      views: 153080,
      unique_sessions: 84134,
      conversions: 553,
      revenue: 414370.50072341046,
      conversion_rates: 0.011702360760572296,
      revenue_per_session: 2.5548077290152045,
      average_order_value: 237.80430298014966,
    },
  ],
  [
    "Health / Nutrition:0-50",
    {
      views: 16403.5,
      unique_sessions: 11514,
      conversions: 232,
      revenue: 7659.733353110934,
      conversion_rates: 0.02010105578681632,
      revenue_per_session: 0.8525188326560731,
      average_order_value: 41.114157187910365,
    },
  ],
  [
    "Health / Nutrition:50-100",
    {
      views: 31970,
      unique_sessions: 18442,
      conversions: 361,
      revenue: 25654.879999999997,
      conversion_rates: 0.02219479249149937,
      revenue_per_session: 1.5330236200230356,
      average_order_value: 72.02898085237797,
    },
  ],
  [
    "Health / Nutrition:100+",
    {
      views: 46598,
      unique_sessions: 31246,
      conversions: 599,
      revenue: 96635.07000000004,
      conversion_rates: 0.020866670933879537,
      revenue_per_session: 2.9077884288386318,
      average_order_value: 119.27304637779828,
    },
  ],
  [
    "Beauty & Fitness / Face & Body Care:0-50",
    {
      views: 15139.5,
      unique_sessions: 10738,
      conversions: 232.5,
      revenue: 7771.945,
      conversion_rates: 0.018484415965422236,
      revenue_per_session: 0.6774381954657278,
      average_order_value: 38.29626345634797,
    },
  ],
  [
    "Beauty & Fitness / Face & Body Care:50-100",
    {
      views: 44380.5,
      unique_sessions: 21081,
      conversions: 523,
      revenue: 40447.44497824188,
      conversion_rates: 0.029508386359741207,
      revenue_per_session: 1.934268498258307,
      average_order_value: 68.430920368268,
    },
  ],
  [
    "Beauty & Fitness / Face & Body Care:100+",
    {
      views: 24597,
      unique_sessions: 12928,
      conversions: 232,
      revenue: 32837.51002666507,
      conversion_rates: 0.01990342003645505,
      revenue_per_session: 2.434309829059829,
      average_order_value: 123.68711102312922,
    },
  ],
  [
    "Health / Women's Health:0-50",
    {
      views: 7620,
      unique_sessions: 4971,
      conversions: 22.5,
      revenue: 1110.1813491505786,
      conversion_rates: 0.01246017533182589,
      revenue_per_session: 0.5688228058735154,
      average_order_value: 45.238186475416654,
    },
  ],
  [
    "Health / Women's Health:50-100",
    {
      views: 17429,
      unique_sessions: 11285.5,
      conversions: 186,
      revenue: 13829.438968643757,
      conversion_rates: 0.022554613921834173,
      revenue_per_session: 1.5449098478817178,
      average_order_value: 68.35333699626776,
    },
  ],
  [
    "Health / Women's Health:100+",
    {
      views: 34042,
      unique_sessions: 16087,
      conversions: 171,
      revenue: 60835.17999999999,
      conversion_rates: 0.010629701000808105,
      revenue_per_session: 3.7816361036862056,
      average_order_value: 355.76128654970756,
    },
  ],
  [
    "Food & Drink / Food:0-50",
    {
      views: 29277,
      unique_sessions: 16605,
      conversions: 174,
      revenue: 6337.39,
      conversion_rates: 0.023453295592397897,
      revenue_per_session: 0.9628647512058384,
      average_order_value: 39.32621257388064,
    },
  ],
  [
    "Food & Drink / Food:50-100",
    {
      views: 42942,
      unique_sessions: 20147.5,
      conversions: 594,
      revenue: 36907.520000000004,
      conversion_rates: 0.02165171719419166,
      revenue_per_session: 1.5963323552601778,
      average_order_value: 66.57932342484989,
    },
  ],
  [
    "Food & Drink / Food:100+",
    {
      views: 58122,
      unique_sessions: 22392.5,
      conversions: 479.5,
      revenue: 87268.80129011121,
      conversion_rates: 0.018673861506207347,
      revenue_per_session: 3.50472261801342,
      average_order_value: 209.68357872912642,
    },
  ],
  [
    "Apparel / Casual Apparel:0-50",
    {
      views: 4530,
      unique_sessions: 2793.5,
      conversions: 3,
      revenue: 132.595,
      conversion_rates: 0.002309759335388832,
      revenue_per_session: 0.10185401678657074,
      average_order_value: 42.98784219391101,
    },
  ],
  [
    "Apparel / Casual Apparel:50-100",
    {
      views: 64467,
      unique_sessions: 23372,
      conversions: 591,
      revenue: 46582.58995140494,
      conversion_rates: 0.0219547456544397,
      revenue_per_session: 1.3423216998935232,
      average_order_value: 66.7115544436692,
    },
  ],
  [
    "Apparel / Casual Apparel:100+",
    {
      views: 59794,
      unique_sessions: 31873,
      conversions: 287,
      revenue: 36072.47205745724,
      conversion_rates: 0.008970560175987968,
      revenue_per_session: 1.96585725630602,
      average_order_value: 150.7288888888889,
    },
  ],
  [
    "Pets & Animals / Dogs:0-50",
    {
      views: 7890.5,
      unique_sessions: 4907,
      conversions: 260.5,
      revenue: 9829.311641148764,
      conversion_rates: 0.02896536923893886,
      revenue_per_session: 1.1012888309707014,
      average_order_value: 42.0279899159634,
    },
  ],
  [
    "Pets & Animals / Dogs:50-100",
    {
      views: 22258,
      unique_sessions: 12078,
      conversions: 331,
      revenue: 16859.31,
      conversion_rates: 0.024196827978853194,
      revenue_per_session: 1.5509944802207913,
      average_order_value: 62.806704545454544,
    },
  ],
  [
    "Pets & Animals / Dogs:100+",
    {
      views: 10327.5,
      unique_sessions: 7399.5,
      conversions: 181,
      revenue: 18985.616683726883,
      conversion_rates: 0.02016694529088204,
      revenue_per_session: 2.3102208253851897,
      average_order_value: 118.6406618337087,
    },
  ],
  [
    "Home & Garden / Home Improvement:0-50",
    {
      views: 22156,
      unique_sessions: 13356,
      conversions: 0,
      revenue: 0,
      conversion_rates: 0,
      revenue_per_session: 0,
      average_order_value: 0,
    },
  ],
  [
    "Home & Garden / Home Improvement:50-100",
    {
      views: 57195.5,
      unique_sessions: 35201.5,
      conversions: 690.5,
      revenue: 57463.35934985828,
      conversion_rates: 0.02064894156563333,
      revenue_per_session: 1.5489613569160015,
      average_order_value: 83.39879787279231,
    },
  ],
  [
    "Home & Garden / Home Improvement:100+",
    {
      views: 17811,
      unique_sessions: 12595,
      conversions: 69.5,
      revenue: 24156.636084948776,
      conversion_rates: 0.007806259526660644,
      revenue_per_session: 2.2749157957490826,
      average_order_value: 253.01146513487464,
    },
  ],
  [
    "Apparel / Athletic Apparel:0-50",
    {
      views: 325382,
      unique_sessions: 125622.5,
      conversions: 4244.5,
      revenue: 209101.87821171942,
      conversion_rates: 0.02284334102588954,
      revenue_per_session: 1.118119625762486,
      average_order_value: 48.64077487855262,
    },
  ],
  [
    "Apparel / Athletic Apparel:50-100",
    {
      views: 23785.5,
      unique_sessions: 10003,
      conversions: 175,
      revenue: 13130.845153690589,
      conversion_rates: 0.019813813402316194,
      revenue_per_session: 1.6143449609142848,
      average_order_value: 76.10370930325365,
    },
  ],
  [
    "Apparel / Athletic Apparel:100+",
    {
      views: 30492,
      unique_sessions: 13167,
      conversions: 197,
      revenue: 27823.51750882249,
      conversion_rates: 0.008881786762793118,
      revenue_per_session: 1.4672590906219525,
      average_order_value: 138.98142523619484,
    },
  ],
  [
    "Beauty & Fitness / Fitness:0-50",
    {
      views: 4471,
      unique_sessions: 2877,
      conversions: 37,
      revenue: 1748.8437943655515,
      conversion_rates: 0.02199736031676199,
      revenue_per_session: 0.7762887629209737,
      average_order_value: 22.38548076923077,
    },
  ],
  [
    "Beauty & Fitness / Fitness:50-100",
    {
      views: 29101,
      unique_sessions: 15374,
      conversions: 204,
      revenue: 14886.802862226226,
      conversion_rates: 0.01235811673047216,
      revenue_per_session: 0.6523501634580458,
      average_order_value: 67.68138033465839,
    },
  ],
  [
    "Beauty & Fitness / Fitness:100+",
    {
      views: 40450,
      unique_sessions: 30668,
      conversions: 200,
      revenue: 81118.94454501463,
      conversion_rates: 0.013083001996271427,
      revenue_per_session: 2.51073206763138,
      average_order_value: 166.5558163265306,
    },
  ],
  [
    "Health / Medical Devices & Equipment:0-50",
    {
      views: 12848,
      unique_sessions: 7514,
      conversions: 0,
      revenue: 0,
      conversion_rates: 0,
      revenue_per_session: 0,
      average_order_value: 0,
    },
  ],
  [
    "Health / Medical Devices & Equipment:50-100",
    {
      views: 75010,
      unique_sessions: 36732,
      conversions: 462,
      revenue: 30719.090000000004,
      conversion_rates: 0.02585737922825586,
      revenue_per_session: 1.9531377284431484,
      average_order_value: 63.63785793241334,
    },
  ],
  [
    "Health / Medical Devices & Equipment:100+",
    {
      views: 33794,
      unique_sessions: 26569,
      conversions: 140,
      revenue: 73615.42,
      conversion_rates: 0.00900163666121113,
      revenue_per_session: 3.4458649548139184,
      average_order_value: 275.1079951960298,
    },
  ],
  [
    "Autos & Vehicles / Parts & Services:0-50",
    {
      views: 86603,
      unique_sessions: 42839,
      conversions: 864,
      revenue: 22985.671489141674,
      conversion_rates: 0.020168538014426106,
      revenue_per_session: 0.5365594782591021,
      average_order_value: 26.603786445765827,
    },
  ],
  [
    "Autos & Vehicles / Parts & Services:50-100",
    {
      views: 83701.5,
      unique_sessions: 34711.5,
      conversions: 522.5,
      revenue: 44140.55626199871,
      conversion_rates: 0.019166988433646264,
      revenue_per_session: 1.5331348491375416,
      average_order_value: 77.81813325347042,
    },
  ],
  [
    "Autos & Vehicles / Parts & Services:100+",
    {
      views: 102090.5,
      unique_sessions: 51358.5,
      conversions: 504,
      revenue: 158081.7363706458,
      conversion_rates: 0.009288357216816725,
      revenue_per_session: 2.86750461922406,
      average_order_value: 230.89597066927175,
    },
  ],
  [
    "People & Society / Religion & Belief:0-50",
    {
      views: 78497.5,
      unique_sessions: 34629,
      conversions: 4567.5,
      revenue: 57359.560000000005,
      conversion_rates: 0.06792131148565118,
      revenue_per_session: 0.8710350120092794,
      average_order_value: 23.27175134128983,
    },
  ],
  [
    "People & Society / Religion & Belief:50-100",
    {
      views: 44442,
      unique_sessions: 25766.5,
      conversions: 544.5,
      revenue: 30800.557593184745,
      conversion_rates: 0.025252794285471197,
      revenue_per_session: 1.7278994482738108,
      average_order_value: 69.69106899690613,
    },
  ],
  [
    "People & Society / Religion & Belief:100+",
    {
      views: 270722,
      unique_sessions: 138978,
      conversions: 984,
      revenue: 212007.74918875462,
      conversion_rates: 0.005576422167537308,
      revenue_per_session: 1.1656467139743238,
      average_order_value: 215.45502966336852,
    },
  ],
  [
    "Home & Garden:0-50",
    {
      views: 4908,
      unique_sessions: 2072,
      conversions: 41,
      revenue: 1379.8435985344158,
      conversion_rates: 0.028413028413028413,
      revenue_per_session: 0.9562325700169202,
      average_order_value: 31.87932897111722,
    },
  ],
  [
    "Home & Garden:50-100",
    {
      views: 41807,
      unique_sessions: 24286,
      conversions: 1237,
      revenue: 76367.51999999996,
      conversion_rates: 0.03257575757575758,
      revenue_per_session: 2.6136823574949513,
      average_order_value: 78.48897805764562,
    },
  ],
  [
    "Home & Garden:100+",
    {
      views: 61706,
      unique_sessions: 37082,
      conversions: 342,
      revenue: 79616.60370506314,
      conversion_rates: 0.009420289855072464,
      revenue_per_session: 2.7529318271702596,
      average_order_value: 218.4372251915197,
    },
  ],
  [
    "Apparel / Clothing Accessories:0-50",
    {
      views: 19705,
      unique_sessions: 10942,
      conversions: 426,
      revenue: 15107.422533029183,
      conversion_rates: 0.018666666666666668,
      revenue_per_session: 0.8068593146106297,
      average_order_value: 42,
    },
  ],
  [
    "Apparel / Clothing Accessories:50-100",
    {
      views: 94544,
      unique_sessions: 51538,
      conversions: 431,
      revenue: 27985.28107327103,
      conversion_rates: 0.009977559998478644,
      revenue_per_session: 0.7283915762409231,
      average_order_value: 69.4717766721513,
    },
  ],
  [
    "Apparel / Clothing Accessories:100+",
    {
      views: 75608.5,
      unique_sessions: 30428,
      conversions: 172.5,
      revenue: 43669.24410799823,
      conversion_rates: 0.006230037303508204,
      revenue_per_session: 1.582310565370872,
      average_order_value: 212.28311386093145,
    },
  ],
  [
    "Toys & Hobbies:0-50",
    {
      views: 32893.5,
      unique_sessions: 9779.5,
      conversions: 187,
      revenue: 6856.319719980896,
      conversion_rates: 0.0210042111011396,
      revenue_per_session: 0.8258112256380712,
      average_order_value: 36.29874359394108,
    },
  ],
  [
    "Toys & Hobbies:50-100",
    {
      views: 19578,
      unique_sessions: 11739,
      conversions: 310,
      revenue: 23624.35,
      conversion_rates: 0.01621143273165466,
      revenue_per_session: 1.1293323495270902,
      average_order_value: 77.64074074074075,
    },
  ],
  [
    "Toys & Hobbies:100+",
    {
      views: 19446,
      unique_sessions: 12493.5,
      conversions: 84.5,
      revenue: 26209.572111448433,
      conversion_rates: 0.010267139073449795,
      revenue_per_session: 2.1035371913562404,
      average_order_value: 252.7825198905643,
    },
  ],
  [
    "Beauty & Fitness / Hair Care:0-50",
    {
      views: 57571,
      unique_sessions: 34391,
      conversions: 1084,
      revenue: 43803.92145088178,
      conversion_rates: 0.030352748154224774,
      revenue_per_session: 1.3051745636828018,
      average_order_value: 43.00021062511717,
    },
  ],
  [
    "Beauty & Fitness / Hair Care:50-100",
    {
      views: 43746,
      unique_sessions: 29157,
      conversions: 502,
      revenue: 33485.950000000004,
      conversion_rates: 0.01917497161599596,
      revenue_per_session: 1.4460400742755972,
      average_order_value: 65.96497282608696,
    },
  ],
  [
    "Beauty & Fitness / Hair Care:100+",
    {
      views: 53417.5,
      unique_sessions: 20785.5,
      conversions: 370,
      revenue: 56261.48477680705,
      conversion_rates: 0.018667049673780526,
      revenue_per_session: 2.873840406966192,
      average_order_value: 187.43188065719244,
    },
  ],
  [
    "Food & Drink / Beverages:0-50",
    {
      views: 31148,
      unique_sessions: 15057,
      conversions: 62,
      revenue: 1591.59,
      conversion_rates: 0.011612957794130085,
      revenue_per_session: 0.5597663788725241,
      average_order_value: 37.642193016869335,
    },
  ],
  [
    "Food & Drink / Beverages:50-100",
    {
      views: 39003,
      unique_sessions: 19440,
      conversions: 434,
      revenue: 32605.520000000004,
      conversion_rates: 0.021347529171371726,
      revenue_per_session: 1.5412780702696895,
      average_order_value: 67.93218649517685,
    },
  ],
  [
    "Food & Drink / Beverages:100+",
    {
      views: 35968,
      unique_sessions: 20494,
      conversions: 140,
      revenue: 26372.616769868757,
      conversion_rates: 0.008477161525016888,
      revenue_per_session: 1.4343860518421665,
      average_order_value: 210.43062499999996,
    },
  ],
  [
    "Consumer Electronics:0-50",
    {
      views: 16674,
      unique_sessions: 11598.5,
      conversions: 12.5,
      revenue: 375.7346595587566,
      conversion_rates: 0.00005434877128297883,
      revenue_per_session: 0.0016336573660357425,
      average_order_value: 15.029386382350264,
    },
  ],
  [
    "Consumer Electronics:50-100",
    {
      views: 28875.5,
      unique_sessions: 21763,
      conversions: 492,
      revenue: 27496.369999999995,
      conversion_rates: 0.01092564727449388,
      revenue_per_session: 0.9343629400323088,
      average_order_value: 69.04442126572826,
    },
  ],
  [
    "Consumer Electronics:100+",
    {
      views: 110408,
      unique_sessions: 59835,
      conversions: 362,
      revenue: 120019.59488749865,
      conversion_rates: 0.009501543766096375,
      revenue_per_session: 2.0001510876904645,
      average_order_value: 227.88200981637448,
    },
  ],
  [
    "Health:0-50",
    {
      views: 38313.5,
      unique_sessions: 26530.5,
      conversions: 68,
      revenue: 2061.238563549483,
      conversion_rates: 0.007277398373204622,
      revenue_per_session: 0.23009035501569974,
      average_order_value: 21.9918414787217,
    },
  ],
  [
    "Health:50-100",
    {
      views: 29137.5,
      unique_sessions: 21420,
      conversions: 482.5,
      revenue: 35360.552411890894,
      conversion_rates: 0.02323144115829999,
      revenue_per_session: 1.5222189856215484,
      average_order_value: 77.31124202198686,
    },
  ],
  [
    "Health:100+",
    {
      views: 32191,
      unique_sessions: 17096,
      conversions: 354,
      revenue: 66271.40999999999,
      conversion_rates: 0.017570442029196654,
      revenue_per_session: 2.546790697674419,
      average_order_value: 152.09445086705202,
    },
  ],
  [
    "Home & Garden / Kitchen & Dining:0-50",
    {
      views: 7880.5,
      unique_sessions: 3344.5,
      conversions: 50,
      revenue: 1168.8927586011478,
      conversion_rates: 0.011528084068953354,
      revenue_per_session: 0.19663742978840293,
      average_order_value: 18.089386381946905,
    },
  ],
  [
    "Home & Garden / Kitchen & Dining:50-100",
    {
      views: 94195,
      unique_sessions: 29479,
      conversions: 954,
      revenue: 73385.96999999999,
      conversion_rates: 0.028538098589713512,
      revenue_per_session: 2.1255994253442982,
      average_order_value: 76.61579264947886,
    },
  ],
  [
    "Home & Garden / Kitchen & Dining:100+",
    {
      views: 87236,
      unique_sessions: 50342,
      conversions: 303,
      revenue: 52520.972144167725,
      conversion_rates: 0.006018831194628739,
      revenue_per_session: 1.5726126011311348,
      average_order_value: 248.72140350877194,
    },
  ],
  [
    "Health / Health Conditions:50-100",
    {
      views: 9833,
      unique_sessions: 7338,
      conversions: 289,
      revenue: 23093.11812723417,
      conversion_rates: 0.03611769872639438,
      revenue_per_session: 2.082935404742437,
      average_order_value: 65.04076595744682,
    },
  ],
  [
    "Health / Health Conditions:100+",
    {
      views: 6827,
      unique_sessions: 4946,
      conversions: 66,
      revenue: 10772.920000000002,
      conversion_rates: 0.009704811969268095,
      revenue_per_session: 2.3309080464167375,
      average_order_value: 224.43583333333336,
    },
  ],
  [
    "Smoking & Vaping / Cannabis:0-50",
    {
      views: 8792,
      unique_sessions: 6169,
      conversions: 0,
      revenue: 0,
      conversion_rates: 0,
      revenue_per_session: 0,
      average_order_value: 0,
    },
  ],
  [
    "Smoking & Vaping / Cannabis:50-100",
    {
      views: 25108,
      unique_sessions: 12747,
      conversions: 376.5,
      revenue: 27102.500316986396,
      conversion_rates: 0.028046193131027457,
      revenue_per_session: 2.316108351602465,
      average_order_value: 83.52330128669271,
    },
  ],
  [
    "Smoking & Vaping / Cannabis:100+",
    {
      views: 50646,
      unique_sessions: 15302,
      conversions: 533,
      revenue: 71489.80999999998,
      conversion_rates: 0.020991828684136377,
      revenue_per_session: 5.476808344070592,
      average_order_value: 117.59132420091325,
    },
  ],
  [
    "Apparel:0-50",
    {
      views: 28870,
      unique_sessions: 24332,
      conversions: 0,
      revenue: 0,
      conversion_rates: 0,
      revenue_per_session: 0,
      average_order_value: 0,
    },
  ],
  [
    "Apparel:50-100",
    {
      views: 22875,
      unique_sessions: 14903,
      conversions: 232,
      revenue: 14970.240000000002,
      conversion_rates: 0.015567335435818291,
      revenue_per_session: 1.3594464555052792,
      average_order_value: 67.22412176008787,
    },
  ],
  [
    "Apparel:100+",
    {
      views: 659908.5,
      unique_sessions: 260209,
      conversions: 3787,
      revenue: 599053.4084953277,
      conversion_rates: 0.016523412042275358,
      revenue_per_session: 3.0925980382347005,
      average_order_value: 216.2939280640212,
    },
  ],
  [
    "Consumer Electronics / Mobile & Wireless:0-50",
    {
      views: 4643,
      unique_sessions: 2665,
      conversions: 0,
      revenue: 0,
      conversion_rates: 0,
      revenue_per_session: 0,
      average_order_value: 0,
    },
  ],
  [
    "Consumer Electronics / Mobile & Wireless:50-100",
    {
      views: 78306,
      unique_sessions: 32885,
      conversions: 639,
      revenue: 46443.47891580122,
      conversion_rates: 0.006537935228827733,
      revenue_per_session: 0.49947173866538136,
      average_order_value: 75.46087524526854,
    },
  ],
  [
    "Consumer Electronics / Mobile & Wireless:100+",
    {
      views: 199493.5,
      unique_sessions: 94999.5,
      conversions: 438,
      revenue: 69918.55248242155,
      conversion_rates: 0.00727898346023244,
      revenue_per_session: 1.205372636612227,
      average_order_value: 115.73974729367235,
    },
  ],
  [
    "Home & Garden / Home & Interior Decor:0-50",
    {
      views: 3991,
      unique_sessions: 3203,
      conversions: 81,
      revenue: 3498.4917536408298,
      conversion_rates: 0.025288791757727132,
      revenue_per_session: 1.0922546842462784,
      average_order_value: 43.19125621778802,
    },
  ],
  [
    "Home & Garden / Home & Interior Decor:50-100",
    {
      views: 11261.5,
      unique_sessions: 5590,
      conversions: 55.5,
      revenue: 5275.244982344325,
      conversion_rates: 0.017963327313930577,
      revenue_per_session: 1.4548513078858436,
      average_order_value: 74.01947473122307,
    },
  ],
  [
    "Home & Garden / Home & Interior Decor:100+",
    {
      views: 165541,
      unique_sessions: 81685,
      conversions: 1047,
      revenue: 223928.35374244564,
      conversion_rates: 0.010979400169534855,
      revenue_per_session: 2.5820418215166794,
      average_order_value: 217.6579166666667,
    },
  ],
  [
    "Home & Garden / Gardening & Landscaping:0-50",
    {
      views: 14275,
      unique_sessions: 7991,
      conversions: 0,
      revenue: 0,
      conversion_rates: 0,
      revenue_per_session: 0,
      average_order_value: 0,
    },
  ],
  [
    "Home & Garden / Gardening & Landscaping:50-100",
    {
      views: 37914.5,
      unique_sessions: 27079,
      conversions: 383,
      revenue: 25750.034999999996,
      conversion_rates: 0.017226934490943695,
      revenue_per_session: 1.268809701885399,
      average_order_value: 66.95262286622888,
    },
  ],
  [
    "Home & Garden / Gardening & Landscaping:100+",
    {
      views: 56615.5,
      unique_sessions: 32615.5,
      conversions: 384.5,
      revenue: 53000.267183723685,
      conversion_rates: 0.00903699847168174,
      revenue_per_session: 1.9402130906508315,
      average_order_value: 164.35672747178882,
    },
  ],
  [
    "Home & Garden / Cleaning:0-50",
    {
      views: 29411,
      unique_sessions: 15379,
      conversions: 324,
      revenue: 9572.702366483341,
      conversion_rates: 0.023178157532901197,
      revenue_per_session: 0.8386690411563035,
      average_order_value: 36.19097249714923,
    },
  ],
  [
    "Home & Garden / Cleaning:50-100",
    {
      views: 5933,
      unique_sessions: 4861,
      conversions: 66,
      revenue: 5698.575232974376,
      conversion_rates: 0.022757111597374178,
      revenue_per_session: 1.8608137541258232,
      average_order_value: 65.19493779076036,
    },
  ],
  [
    "Home & Garden / Cleaning:100+",
    {
      views: 27733.5,
      unique_sessions: 20360.5,
      conversions: 398,
      revenue: 54684.388834751684,
      conversion_rates: 0.019311248683656426,
      revenue_per_session: 2.5529993796221895,
      average_order_value: 143.52712929612488,
    },
  ],
  [
    "Apparel / Footwear:0-50",
    {
      views: 74055.5,
      unique_sessions: 32384.5,
      conversions: 1561,
      revenue: 71972.5247652645,
      conversion_rates: 0.0575575712581453,
      revenue_per_session: 2.2653833004232444,
      average_order_value: 42.819627474612645,
    },
  ],
  [
    "Apparel / Footwear:50-100",
    {
      views: 11589,
      unique_sessions: 6136,
      conversions: 76.5,
      revenue: 7337.563326114536,
      conversion_rates: 0.017978537511773147,
      revenue_per_session: 1.3262436604622225,
      average_order_value: 82.85038839699044,
    },
  ],
  [
    "Apparel / Footwear:100+",
    {
      views: 175335,
      unique_sessions: 93020.5,
      conversions: 1621.5,
      revenue: 232306.09841957787,
      conversion_rates: 0.018166434671403595,
      revenue_per_session: 2.535565722592912,
      average_order_value: 142.40818908339182,
    },
  ],
  [
    "Beauty & Fitness / Spas & Beauty Services:50-100",
    {
      views: 9765,
      unique_sessions: 6417,
      conversions: 51,
      revenue: 3384.7999999999997,
      conversion_rates: 0.01276595744680851,
      revenue_per_session: 0.7473021276595744,
      average_order_value: 58.538666666666664,
    },
  ],
  [
    "Beauty & Fitness / Spas & Beauty Services:100+",
    {
      views: 13530,
      unique_sessions: 9618,
      conversions: 180,
      revenue: 53754.24772436819,
      conversion_rates: 0.010567362058880873,
      revenue_per_session: 2.5180689405712053,
      average_order_value: 297.5355555555555,
    },
  ],
  [
    "Books & Literature / Notebooks & Planners:0-50",
    {
      views: 12837,
      unique_sessions: 4502,
      conversions: 132,
      revenue: 5564.639555783444,
      conversion_rates: 0.025709219858156027,
      revenue_per_session: 1.0230504670420386,
      average_order_value: 39.79313540770412,
    },
  ],
  [
    "Books & Literature / Notebooks & Planners:50-100",
    {
      views: 113113,
      unique_sessions: 41262,
      conversions: 697,
      revenue: 43727.87492089216,
      conversion_rates: 0.016892055644418594,
      revenue_per_session: 1.204184479666521,
      average_order_value: 71.2870301291248,
    },
  ],
  [
    "Adult:0-50",
    {
      views: 65981,
      unique_sessions: 27549.5,
      conversions: 350,
      revenue: 13746.086983400623,
      conversion_rates: 0.014969429493586296,
      revenue_per_session: 0.6057714978827132,
      average_order_value: 41.632971487041615,
    },
  ],
  [
    "Adult:50-100",
    {
      views: 290336,
      unique_sessions: 164372.5,
      conversions: 4085,
      revenue: 209312.59499999983,
      conversion_rates: 0.021534018943588147,
      revenue_per_session: 1.0895706834018914,
      average_order_value: 59.20013629184655,
    },
  ],
  [
    "Adult:100+",
    {
      views: 74867,
      unique_sessions: 37042,
      conversions: 184.5,
      revenue: 112432.83508821661,
      conversion_rates: 0.005525710342562444,
      revenue_per_session: 2.135159677199307,
      average_order_value: 431.8190405565049,
    },
  ],
  [
    "Arts & Entertainment / Music & Audio:0-50",
    {
      views: 48746.5,
      unique_sessions: 33659.5,
      conversions: 891.5,
      revenue: 40510.36824718656,
      conversion_rates: 0.013643807104268376,
      revenue_per_session: 0.619983903631511,
      average_order_value: 22.72034113695264,
    },
  ],
  [
    "Arts & Entertainment / Music & Audio:50-100",
    {
      views: 44526,
      unique_sessions: 18393,
      conversions: 213,
      revenue: 15120.296174329154,
      conversion_rates: 0.022373949579831933,
      revenue_per_session: 1.5882664048665078,
      average_order_value: 70.98730598276599,
    },
  ],
  [
    "Arts & Entertainment / Music & Audio:100+",
    {
      views: 44931,
      unique_sessions: 21311.5,
      conversions: 166,
      revenue: 81562.12715552289,
      conversion_rates: 0.009335366907150686,
      revenue_per_session: 2.5068108667299365,
      average_order_value: 285.86151174544267,
    },
  ],
  [
    "Apparel / Women's Clothing:0-50",
    {
      views: 17664.5,
      unique_sessions: 13209.5,
      conversions: 283,
      revenue: 11344.732900220999,
      conversion_rates: 0.0169157202630006,
      revenue_per_session: 0.6781071667794979,
      average_order_value: 20.04369770357067,
    },
  ],
  [
    "Apparel / Women's Clothing:50-100",
    {
      views: 449675,
      unique_sessions: 176787,
      conversions: 1116,
      revenue: 72946.405319925,
      conversion_rates: 0.007743015590666527,
      revenue_per_session: 0.7466844900776541,
      average_order_value: 84.84055187700663,
    },
  ],
  [
    "Apparel / Women's Clothing:100+",
    {
      views: 184402,
      unique_sessions: 54119,
      conversions: 791,
      revenue: 192459.16999999998,
      conversion_rates: 0.014295243678114451,
      revenue_per_session: 2.2731137027380344,
      average_order_value: 178.020587184891,
    },
  ],
  [
    "Apparel / Accessories:0-50",
    {
      views: 213045,
      unique_sessions: 70044,
      conversions: 2701,
      revenue: 125123.21485008654,
      conversion_rates: 0.03856147564388099,
      revenue_per_session: 1.7863516482509072,
      average_order_value: 46.324774102216416,
    },
  ],
  [
    "Apparel / Accessories:50-100",
    {
      views: 56564,
      unique_sessions: 39138,
      conversions: 493,
      revenue: 35439.42256528023,
      conversion_rates: 0.012596453574531145,
      revenue_per_session: 0.9054990690704744,
      average_order_value: 71.88523846912825,
    },
  ],
  [
    "Apparel / Accessories:100+",
    {
      views: 82023,
      unique_sessions: 36861,
      conversions: 191,
      revenue: 71043.6381091553,
      conversion_rates: 0.004833252779120348,
      revenue_per_session: 1.448305340744321,
      average_order_value: 299.65437499999996,
    },
  ],
  [
    "Home & Garden / Bed & Bath:0-50",
    {
      views: 2712,
      unique_sessions: 2213.5,
      conversions: 0,
      revenue: 0,
      conversion_rates: 0,
      revenue_per_session: 0,
      average_order_value: 0,
    },
  ],
  [
    "Home & Garden / Bed & Bath:50-100",
    {
      views: 13339,
      unique_sessions: 7221,
      conversions: 155.5,
      revenue: 10204.154731842567,
      conversion_rates: 0.021687217766729036,
      revenue_per_session: 1.4430515417235372,
      average_order_value: 69.4009343993794,
    },
  ],
  [
    "Home & Garden / Bed & Bath:100+",
    {
      views: 29356,
      unique_sessions: 14863,
      conversions: 91,
      revenue: 57344.22904051801,
      conversion_rates: 0.010739490641301013,
      revenue_per_session: 2.614640007557988,
      average_order_value: 197.3905127374173,
    },
  ],
  [
    "Gifts & Special Events:0-50",
    {
      views: 72719,
      unique_sessions: 64450,
      conversions: 295,
      revenue: 5374.745421698313,
      conversion_rates: 0.0045771916214119475,
      revenue_per_session: 0.08339403292006692,
      average_order_value: 18.219476005756995,
    },
  ],
  [
    "Gifts & Special Events:50-100",
    {
      views: 52380,
      unique_sessions: 30138.5,
      conversions: 624.5,
      revenue: 51550.198576174516,
      conversion_rates: 0.020773237025655325,
      revenue_per_session: 1.3979264762710968,
      average_order_value: 58.69695274762148,
    },
  ],
  [
    "Gifts & Special Events:100+",
    {
      views: 478012.5,
      unique_sessions: 127383,
      conversions: 1405.5,
      revenue: 281260.99426097475,
      conversion_rates: 0.01251437723280861,
      revenue_per_session: 1.6826055736809837,
      average_order_value: 132.75817893091357,
    },
  ],
  [
    "Toys & Hobbies / Arts & Crafts:0-50",
    {
      views: 9378,
      unique_sessions: 6433,
      conversions: 154.5,
      revenue: 6374.806102330131,
      conversion_rates: 0.017225737086870148,
      revenue_per_session: 0.6843519668101075,
      average_order_value: 37.421035213709885,
    },
  ],
  [
    "Toys & Hobbies / Arts & Crafts:50-100",
    {
      views: 109341,
      unique_sessions: 42210,
      conversions: 405,
      revenue: 30509.51206416291,
      conversion_rates: 0.01494661921708185,
      revenue_per_session: 1.2129395017793596,
      average_order_value: 81.15142857142858,
    },
  ],
  [
    "Toys & Hobbies / Arts & Crafts:100+",
    {
      views: 9136,
      unique_sessions: 5016,
      conversions: 63,
      revenue: 7451.939999999999,
      conversion_rates: 0.012559808612440191,
      revenue_per_session: 1.4856339712918658,
      average_order_value: 125.12392776495344,
    },
  ],
  [
    "Health / Mental Health:0-50",
    {
      views: 10631,
      unique_sessions: 8979,
      conversions: 322,
      revenue: 11205.05082067765,
      conversion_rates: 0.03260649087221095,
      revenue_per_session: 1.2479174541349427,
      average_order_value: 38.84341545720034,
    },
  ],
  [
    "Health / Mental Health:50-100",
    {
      views: 114501,
      unique_sessions: 80902,
      conversions: 1755,
      revenue: 92779.51435007305,
      conversion_rates: 0.025544452206664035,
      revenue_per_session: 1.5130282975964826,
      average_order_value: 59.231189823744344,
    },
  ],
  [
    "Health / Mental Health:100+",
    {
      views: 307936,
      unique_sessions: 164986,
      conversions: 1824,
      revenue: 223525.70220251477,
      conversion_rates: 0.011055483495569321,
      revenue_per_session: 1.3548161795698712,
      average_order_value: 122.54698585664187,
    },
  ],
  [
    "Pets & Animals:0-50",
    {
      views: 12194,
      unique_sessions: 8570,
      conversions: 274,
      revenue: 12647.98,
      conversion_rates: 0.017026990734523968,
      revenue_per_session: 0.7393696790653955,
      average_order_value: 39.392812500000005,
    },
  ],
  [
    "Pets & Animals:50-100",
    {
      views: 35065,
      unique_sessions: 22606.5,
      conversions: 796,
      revenue: 76214.67591887122,
      conversion_rates: 0.021310720306992402,
      revenue_per_session: 1.6428143413196317,
      average_order_value: 74.32161702499442,
    },
  ],
  [
    "Pets & Animals:100+",
    {
      views: 55605.5,
      unique_sessions: 25669.5,
      conversions: 287,
      revenue: 45099.09414707206,
      conversion_rates: 0.017608040472332977,
      revenue_per_session: 2.131889752906778,
      average_order_value: 141.20110604368324,
    },
  ],
  [
    "Sports / Individual Sports:50-100",
    {
      views: 12649,
      unique_sessions: 7185,
      conversions: 129.5,
      revenue: 10049.144459569758,
      conversion_rates: 0.017936659090403734,
      revenue_per_session: 1.3753234442925626,
      average_order_value: 70.13409201684854,
    },
  ],
  [
    "Sports / Individual Sports:100+",
    {
      views: 37870.5,
      unique_sessions: 19340.5,
      conversions: 320.5,
      revenue: 69069.96486119993,
      conversion_rates: 0.010852143082359225,
      revenue_per_session: 2.5102076728483116,
      average_order_value: 263.4084484247754,
    },
  ],
  [
    "Health / Oral & Dental Care:0-50",
    {
      views: 19915,
      unique_sessions: 11686.5,
      conversions: 279.5,
      revenue: 11032.290540841444,
      conversion_rates: 0.026174176018048577,
      revenue_per_session: 1.1125448833913418,
      average_order_value: 44.009632813721886,
    },
  ],
  [
    "Health / Oral & Dental Care:50-100",
    {
      views: 35404,
      unique_sessions: 17837,
      conversions: 920,
      revenue: 88610.53751552459,
      conversion_rates: 0.016478286734086852,
      revenue_per_session: 1.0941428715047599,
      average_order_value: 69.23305711152089,
    },
  ],
  [
    "Health / Oral & Dental Care:100+",
    {
      views: 149617,
      unique_sessions: 79860,
      conversions: 1725,
      revenue: 241460.43000000002,
      conversion_rates: 0.02093025423277782,
      revenue_per_session: 2.8652123405304275,
      average_order_value: 121.85232690128694,
    },
  ],
  [
    "Travel:0-50",
    {
      views: 794729,
      unique_sessions: 413512,
      conversions: 6260,
      revenue: 88445.21792525769,
      conversion_rates: 0.015138617500822226,
      revenue_per_session: 0.21388791117369674,
      average_order_value: 14.128629061542762,
    },
  ],
  [
    "Travel:50-100",
    {
      views: 192350,
      unique_sessions: 121407,
      conversions: 7335,
      revenue: 724469.2915747913,
      conversion_rates: 0.060416615186933205,
      revenue_per_session: 5.967277764665886,
      average_order_value: 98.768819573932,
    },
  ],
  [
    "Travel:100+",
    {
      views: 88500,
      unique_sessions: 39130,
      conversions: 248,
      revenue: 145921.51,
      conversion_rates: 0.006337848198313315,
      revenue_per_session: 3.729146690518784,
      average_order_value: 193.2367402286626,
    },
  ],
  [
    "Arts & Entertainment / Visual Art & Design:0-50",
    {
      views: 2984,
      unique_sessions: 1810,
      conversions: 0,
      revenue: 0,
      conversion_rates: 0,
      revenue_per_session: 0,
      average_order_value: 0,
    },
  ],
  [
    "Arts & Entertainment / Visual Art & Design:50-100",
    {
      views: 13097,
      unique_sessions: 7979,
      conversions: 156,
      revenue: 12584.16,
      conversion_rates: 0.009685966461756224,
      revenue_per_session: 0.7444384203878037,
      average_order_value: 80.6676923076923,
    },
  ],
  [
    "Arts & Entertainment / Visual Art & Design:100+",
    {
      views: 8564,
      unique_sessions: 6063,
      conversions: 69,
      revenue: 7974.7,
      conversion_rates: 0.006002728512960437,
      revenue_per_session: 1.3153059541481114,
      average_order_value: 213.98636363636362,
    },
  ],
  [
    "Apparel / Children's Clothing:50-100",
    {
      views: 42575.5,
      unique_sessions: 21836,
      conversions: 685.5,
      revenue: 49773.18042460735,
      conversion_rates: 0.023467347497465055,
      revenue_per_session: 1.4081113740591704,
      average_order_value: 71.39089114567636,
    },
  ],
  [
    "Apparel / Children's Clothing:100+",
    {
      views: 70151.5,
      unique_sessions: 25846,
      conversions: 299,
      revenue: 47512.99748136704,
      conversion_rates: 0.010552139283222815,
      revenue_per_session: 2.2496536564008514,
      average_order_value: 195.73253769636815,
    },
  ],
  [
    "Home & Garden / Home Appliances:0-50",
    {
      views: 226514,
      unique_sessions: 157827,
      conversions: 5795,
      revenue: 220453.4,
      conversion_rates: 0.03671741843917707,
      revenue_per_session: 1.3968040956236891,
      average_order_value: 38.04200172562554,
    },
  ],
  [
    "Home & Garden / Home Appliances:50-100",
    {
      views: 672863,
      unique_sessions: 355892,
      conversions: 11989,
      revenue: 1194684.4666412575,
      conversion_rates: 0.03368718600024727,
      revenue_per_session: 3.356873620764888,
      average_order_value: 99.64838323807302,
    },
  ],
  [
    "Home & Garden / Home Appliances:100+",
    {
      views: 53668,
      unique_sessions: 35132.5,
      conversions: 366,
      revenue: 52883.70499999999,
      conversion_rates: 0.012625683491752315,
      revenue_per_session: 1.7189721421016666,
      average_order_value: 194.57887412683652,
    },
  ],
  [
    "Health / Reproductive Health:0-50",
    {
      views: 18237,
      unique_sessions: 13839,
      conversions: 42,
      revenue: 730.0900000000001,
      conversion_rates: 0.0030349013657056147,
      revenue_per_session: 0.052755979478286016,
      average_order_value: 17.38309523809524,
    },
  ],
  [
    "Health / Reproductive Health:50-100",
    {
      views: 199579.5,
      unique_sessions: 109346,
      conversions: 1391,
      revenue: 91442.785,
      conversion_rates: 0.017326857736137888,
      revenue_per_session: 1.2538921510100924,
      average_order_value: 72.1967557128666,
    },
  ],
  [
    "Health / Reproductive Health:100+",
    {
      views: 49481,
      unique_sessions: 22165,
      conversions: 314.5,
      revenue: 36490.26799380686,
      conversion_rates: 0.01748345542778735,
      revenue_per_session: 3.3411740732924646,
      average_order_value: 121.50658393824345,
    },
  ],
  [
    "Safety & Survival / Safety:0-50",
    {
      views: 15319,
      unique_sessions: 4054.5,
      conversions: 59,
      revenue: 2224.8202833860255,
      conversion_rates: 0.031569423314110355,
      revenue_per_session: 1.1876315109585645,
      average_order_value: 38.82774062547047,
    },
  ],
  [
    "Safety & Survival / Safety:50-100",
    {
      views: 360105,
      unique_sessions: 333807,
      conversions: 8815,
      revenue: 667660.1660070012,
      conversion_rates: 0.02640747497805618,
      revenue_per_session: 2.0001383014945797,
      average_order_value: 75.74136880397064,
    },
  ],
  [
    "Safety & Survival / Safety:100+",
    {
      views: 72760,
      unique_sessions: 36127,
      conversions: 518.5,
      revenue: 58807.20988921978,
      conversion_rates: 0.008618557157743626,
      revenue_per_session: 0.9874666029269771,
      average_order_value: 117.27119216551688,
    },
  ],
  [
    "Apparel / Undergarments:0-50",
    {
      views: 53814,
      unique_sessions: 46110,
      conversions: 13.5,
      revenue: 330.9800065005389,
      conversion_rates: 0.006598240469208211,
      revenue_per_session: 0.16176930914004833,
      average_order_value: 12.258518759279218,
    },
  ],
  [
    "Apparel / Undergarments:50-100",
    {
      views: 73265,
      unique_sessions: 37254,
      conversions: 843,
      revenue: 63795.506176060175,
      conversion_rates: 0.02281298988887353,
      revenue_per_session: 1.6090925115177739,
      average_order_value: 82.41514678190137,
    },
  ],
  [
    "Apparel / Undergarments:100+",
    {
      views: 123923.5,
      unique_sessions: 50548,
      conversions: 366.5,
      revenue: 41847.06071571667,
      conversion_rates: 0.007248353746282389,
      revenue_per_session: 0.8276896249909086,
      average_order_value: 117.10329349511395,
    },
  ],
  [
    "Health / Sleep:0-50",
    {
      views: 7808,
      unique_sessions: 2179,
      conversions: 51,
      revenue: 2459.312811959863,
      conversion_rates: 0.023405231757687012,
      revenue_per_session: 1.128642869187638,
      average_order_value: 48.22181984235026,
    },
  ],
  [
    "Health / Sleep:50-100",
    {
      views: 9189.5,
      unique_sessions: 7086,
      conversions: 277,
      revenue: 14567.495000000004,
      conversion_rates: 0.022449236822367467,
      revenue_per_session: 1.6875064601653469,
      average_order_value: 74.56099593943779,
    },
  ],
  [
    "Health / Sleep:100+",
    {
      views: 329942.5,
      unique_sessions: 182829,
      conversions: 4199.5,
      revenue: 468596.76902085415,
      conversion_rates: 0.015560681265770197,
      revenue_per_session: 2.3378954590117464,
      average_order_value: 197.57815876034675,
    },
  ],
  [
    "Apparel / Eyewear:0-50",
    {
      views: 2012,
      unique_sessions: 1966,
      conversions: 0,
      revenue: 0,
      conversion_rates: 0,
      revenue_per_session: 0,
      average_order_value: 0,
    },
  ],
  [
    "Apparel / Eyewear:50-100",
    {
      views: 3169,
      unique_sessions: 2126,
      conversions: 31,
      revenue: 2110.9477031450992,
      conversion_rates: 0.021723896285914507,
      revenue_per_session: 1.4792906118746316,
      average_order_value: 68.09508719822901,
    },
  ],
  [
    "Apparel / Eyewear:100+",
    {
      views: 80288.5,
      unique_sessions: 31963,
      conversions: 370.5,
      revenue: 56748.80897170275,
      conversion_rates: 0.008377387521312122,
      revenue_per_session: 1.3849453491115966,
      average_order_value: 139.38788603160233,
    },
  ],
  [
    "Business & Industrial / Business Services:0-50",
    {
      views: 17093.5,
      unique_sessions: 11544,
      conversions: 1841,
      revenue: 2597.7097771150525,
      conversion_rates: 0.09591667631211444,
      revenue_per_session: 0.44859997866998813,
      average_order_value: 22.654156085926193,
    },
  ],
  [
    "Business & Industrial / Business Services:50-100",
    {
      views: 11505,
      unique_sessions: 6241,
      conversions: 74,
      revenue: 4926.956969487792,
      conversion_rates: 0.008502388805906336,
      revenue_per_session: 0.5765640091026258,
      average_order_value: 71.87034324864695,
    },
  ],
  [
    "Business & Industrial / Business Services:100+",
    {
      views: 19153.5,
      unique_sessions: 11843.5,
      conversions: 317,
      revenue: 40627.545008455534,
      conversion_rates: 0.05157168082914154,
      revenue_per_session: 9.972450197030167,
      average_order_value: 163.62410858936775,
    },
  ],
  [
    "Apparel / Men's Clothing:0-50",
    {
      views: 91183,
      unique_sessions: 51729,
      conversions: 699,
      revenue: 30439.90979313136,
      conversion_rates: 0.01351272980339848,
      revenue_per_session: 0.5884496084040163,
      average_order_value: 43.54779655669722,
    },
  ],
  [
    "Apparel / Men's Clothing:100+",
    {
      views: 30442,
      unique_sessions: 15895,
      conversions: 138,
      revenue: 21118.204185931812,
      conversion_rates: 0.012462552426602757,
      revenue_per_session: 2.041371910020707,
      average_order_value: 187.13194379391103,
    },
  ],
  [
    "Consumer Electronics / Audio Equipment:0-50",
    {
      views: 4931,
      unique_sessions: 3288.5,
      conversions: 92.5,
      revenue: 3921.0384711044553,
      conversion_rates: 0.028617611745031447,
      revenue_per_session: 1.1977141718936481,
      average_order_value: 42.63120121591141,
    },
  ],
  [
    "Consumer Electronics / Audio Equipment:50-100",
    {
      views: 19845,
      unique_sessions: 11105,
      conversions: 90,
      revenue: 6764.84053972435,
      conversion_rates: 0.010134484821684052,
      revenue_per_session: 0.8742946792569243,
      average_order_value: 78.82656080613265,
    },
  ],
  [
    "Consumer Electronics / Audio Equipment:100+",
    {
      views: 322653,
      unique_sessions: 231299,
      conversions: 1788,
      revenue: 628517.1699999998,
      conversion_rates: 0.0077302539137653,
      revenue_per_session: 2.410550547093681,
      average_order_value: 286.68499999999995,
    },
  ],
  [
    "Business & Industrial:0-50",
    {
      views: 2840,
      unique_sessions: 2712,
      conversions: 0,
      revenue: 0,
      conversion_rates: 0,
      revenue_per_session: 0,
      average_order_value: 0,
    },
  ],
  [
    "Business & Industrial:50-100",
    {
      views: 305050.5,
      unique_sessions: 114231,
      conversions: 2366,
      revenue: 192832.7059233265,
      conversion_rates: 0.044014571063016206,
      revenue_per_session: 3.313499395311369,
      average_order_value: 79.03975091552317,
    },
  ],
  [
    "Business & Industrial:100+",
    {
      views: 19191,
      unique_sessions: 11663,
      conversions: 36,
      revenue: 23270.549605512493,
      conversion_rates: 0.0045442853468232874,
      revenue_per_session: 2.640878880556314,
      average_order_value: 439.06697368891497,
    },
  ],
  [
    "Autos & Vehicles:0-50",
    {
      views: 1760.5,
      unique_sessions: 1025.5,
      conversions: 68.5,
      revenue: 3327.173800062342,
      conversion_rates: 0.052699628039168486,
      revenue_per_session: 2.4907686787389967,
      average_order_value: 48.11958646703425,
    },
  ],
  [
    "Autos & Vehicles:50-100",
    {
      views: 8437.5,
      unique_sessions: 5540.5,
      conversions: 233.5,
      revenue: 12825.197741914599,
      conversion_rates: 0.03495211229655758,
      revenue_per_session: 2.2806815404398773,
      average_order_value: 58.40143281776311,
    },
  ],
  [
    "Books & Literature:50-100",
    {
      views: 50892,
      unique_sessions: 35488,
      conversions: 586,
      revenue: 31266.452907475985,
      conversion_rates: 0.01989404869251578,
      revenue_per_session: 1.083881090851306,
      average_order_value: 54.482680102168764,
    },
  ],
  [
    "Sports:0-50",
    {
      views: 4348,
      unique_sessions: 3627,
      conversions: 40,
      revenue: 1324.6600000000003,
      conversion_rates: 0.011028398125172319,
      revenue_per_session: 0.3652219465122692,
      average_order_value: 33.11650000000001,
    },
  ],
  [
    "Sports:50-100",
    {
      views: 28494,
      unique_sessions: 16263,
      conversions: 164,
      revenue: 13255.636743670762,
      conversion_rates: 0.015166928696938869,
      revenue_per_session: 1.2258981544132768,
      average_order_value: 64.49362208149014,
    },
  ],
  [
    "Sports:100+",
    {
      views: 104217,
      unique_sessions: 63266,
      conversions: 752,
      revenue: 294191.29985880933,
      conversion_rates: 0.012862810998978277,
      revenue_per_session: 3.8479558591612495,
      average_order_value: 221.88207649486344,
    },
  ],
  [
    "Jobs & Education:50-100",
    {
      views: 320986,
      unique_sessions: 176131,
      conversions: 3546,
      revenue: 298152.50999999995,
      conversion_rates: 0.02013274210672738,
      revenue_per_session: 1.6927883791041891,
      average_order_value: 84.08136209813874,
    },
  ],
  [
    "Jobs & Education:100+",
    {
      views: 8818.5,
      unique_sessions: 4451.5,
      conversions: 24,
      revenue: 15774.049400622822,
      conversion_rates: 0.005316065188011464,
      revenue_per_session: 3.025101171389121,
      average_order_value: 560.5169991779821,
    },
  ],
  [
    "People & Society:0-50",
    {
      views: 13494.5,
      unique_sessions: 9598,
      conversions: 187,
      revenue: 7617.350658748432,
      conversion_rates: 0.02038517872117951,
      revenue_per_session: 0.7177638750087725,
      average_order_value: 36.1773507081882,
    },
  ],
  [
    "People & Society:50-100",
    {
      views: 297674,
      unique_sessions: 188590.5,
      conversions: 8837.5,
      revenue: 466101.23424857634,
      conversion_rates: 0.046314763669522654,
      revenue_per_session: 2.4751461949233216,
      average_order_value: 53.55812383480833,
    },
  ],
  [
    "People & Society:100+",
    {
      views: 310692,
      unique_sessions: 189529,
      conversions: 1425,
      revenue: 192228.38265632364,
      conversion_rates: 0.007518638308649336,
      revenue_per_session: 1.014242583754062,
      average_order_value: 134.89711063601658,
    },
  ],
  [
    "Beauty & Fitness:0-50",
    {
      views: 123267.5,
      unique_sessions: 51878.5,
      conversions: 1566,
      revenue: 65371.41878657655,
      conversion_rates: 0.015369365302136596,
      revenue_per_session: 0.6415818746167625,
      average_order_value: 20.87210050656978,
    },
  ],
  [
    "Beauty & Fitness:50-100",
    {
      views: 143257,
      unique_sessions: 71730,
      conversions: 2231.5,
      revenue: 148047.7149866023,
      conversion_rates: 0.026292712564016094,
      revenue_per_session: 1.693398918111254,
      average_order_value: 65.38057618444041,
    },
  ],
  [
    "Beauty & Fitness:100+",
    {
      views: 294542,
      unique_sessions: 211988,
      conversions: 3219,
      revenue: 918770.2499999994,
      conversion_rates: 0.01518482178236504,
      revenue_per_session: 4.3340672585240645,
      average_order_value: 285.4210158434295,
    },
  ],
  [
    "Internet:0-50",
    {
      views: 2627,
      unique_sessions: 2133,
      conversions: 56,
      revenue: 2546,
      conversion_rates: 0.02625410220346929,
      revenue_per_session: 1.193624003750586,
      average_order_value: 45.464285714285715,
    },
  ],
  [
    "Safety & Survival:50-100",
    {
      views: 1609625,
      unique_sessions: 447975,
      conversions: 12847,
      revenue: 838578.5200000001,
      conversion_rates: 0.028677939617166137,
      revenue_per_session: 1.8719315140353818,
      average_order_value: 65.27426792247218,
    },
  ],
  [
    "Computers:100+",
    {
      views: 110600.5,
      unique_sessions: 52700.5,
      conversions: 291.5,
      revenue: 120347.2973079185,
      conversion_rates: 0.026111369673609316,
      revenue_per_session: 4.263218761080422,
      average_order_value: 344.7356110948845,
    },
  ],
  [
    "Finance:100+",
    {
      views: 37122,
      unique_sessions: 29668,
      conversions: 55,
      revenue: 11579.30979527612,
      conversion_rates: 0.001853849265201564,
      revenue_per_session: 0.3902962719184347,
      average_order_value: 210.5329053686567,
    },
  ],
  [
    "Games:100+",
    {
      views: 40914,
      unique_sessions: 21826,
      conversions: 19,
      revenue: 54274.24,
      conversion_rates: 0.0008705213964995877,
      revenue_per_session: 2.4866782736186197,
      average_order_value: 2856.538947368421,
    },
  ],
];
